import { InAppState } from "../base/InAppState";
import template from "./VerifyRequiredTemplate.html";

import { _lcs, currentLanguage } from "../../i18n";

/**
 * VerifyRequiredState — what the Planer and the Chat show instead of their
 * content when the guest has no confirmed account (see App.hasVerifiedAccount).
 *
 * A screen rather than an inline banner, because there is nothing underneath
 * worth showing: a plan nobody can save and a thread nobody can answer are
 * worse than an honest explanation of what is missing.
 *
 * Two different situations end up here and the copy branches, because telling
 * somebody who never made an account to "check your inbox" is nonsense:
 *
 *   • no account ("Als Gast fortfahren") → offer to create one
 *   • registered, not yet validated      → offer a fresh validation mail
 *
 * `?from=` carries which tab was asked for, so the text can name it.
 *
 * The account may have been confirmed since the session was loaded (the link
 * opened on another device), so an unconfirmed guest's status is asked afresh
 * on entry — confirmed by now, they go straight on to the tab they wanted.
 */
export class VerifyRequiredState extends InAppState
{
    static ROUTE = "/verify-account";
    static ID = "verify-required";

    // A dead end for the tab bar: neither tab is lit, because the guest is not
    // on either of them.
    getActiveTab() { return null; }

    async enter()
    {
        const isAnonymous = this.app.user?.guest === true;
        if ( !isAnonymous && await this._confirmedMeanwhile() )
        {
            await this.app.resolveSession();
            this.app.router.redirect( this._featureRoute() );
            return;
        }

        const feature = this._featureLabel();

        this.renderScreen( template, isAnonymous
            ? {
                icon : "user-round",
                title : _lcs( "Dafür brauchst du ein Konto" ),
                text : _lcs( "{feature} gehört zu dir, nicht zum Haus — deine Auswahl und deine " +
                             "Nachrichten bleiben erhalten, wenn du ein Konto anlegst. Alles andere " +
                             "in der App kannst du weiterhin ohne Konto nutzen.", { feature : feature } ),
                email : "",
                primaryLabel : _lcs( "Konto erstellen" ),
                secondaryLabel : _lcs( "Zurück" ),
                note : _lcs( "Dauert unter einer Minute." )
            }
            : {
                icon : "mail",
                title : _lcs( "Bitte bestätige deine E-Mail" ),
                text : _lcs( "{feature} steht dir zur Verfügung, sobald deine E-Mail-Adresse " +
                             "bestätigt ist. Öffne dazu den Link, den wir dir geschickt haben.",
                    { feature : feature } ),
                email : this.app.user?.email || "",
                primaryLabel : _lcs( "Neuen Link anfordern" ),
                secondaryLabel : _lcs( "Zurück" ),
                note : _lcs( "Nichts angekommen? Sieh auch im Spam-Ordner nach." )
            } );

        this._bind( isAnonymous );
    }

    _bind( isAnonymous )
    {
        const go = ( route ) => this.app.router.redirect( route );
        const back = () => go( "/" );

        this._on( "back", back );
        this._on( "secondary", back );

        this._on( "primary", () =>
        {
            if ( isAnonymous )
            {
                return go( "/signup" );
            }

            this._resend();
        } );
    }

    /** Mail a fresh validation link (the earlier one stops working). */
    async _resend()
    {
        if ( this.sending )
        {
            return;
        }
        const btn = this.app.view.pageContent.querySelector( '[data-id="primary"]' );
        this.sending = true;
        if ( btn ) btn.disabled = true;
        try
        {
            const payload = { email : this.app.user.email, language : currentLanguage() };
            const slug = this.app.theme.getTenantSlug();
            if ( slug ) payload.slug = slug;

            await this.app.api.post( "/auth/user/validation-mail", payload );
            this.app.view.toast( _lcs( "Wir haben dir einen neuen Link an {email} geschickt.", { email : this.app.user.email } ), "success", 5000 );
        }
        catch ( e )
        {
            console.error( e );
            this.app.view.toast( _lcs( "Der Link konnte nicht angefordert werden. Bitte versuche es später erneut." ), "error" );
        }
        // Not re-enabled at once: the API sends at most one mail a minute anyway.
        this.setTimeout( () =>
        {
            this.sending = false;
            if ( btn ) btn.disabled = false;
        }, 30000 );
    }

    /** Whether the account behind the session is confirmed by now. */
    async _confirmedMeanwhile()
    {
        const sessionToken = this.app.storage.loadSessionToken();
        if ( !sessionToken )
        {
            return false;
        }
        try
        {
            const { result } = await this.app.api.get( "/auth/session/" + sessionToken );
            return result?.data?.user?.status === "ok";
        }
        catch ( e )
        {
            return false;
        }
    }

    /** The tab that sent the guest here. */
    _featureRoute()
    {
        switch ( this.routeParams.getQuery( "from", "" ) )
        {
            case "plan": return "/plan";
            case "chat": return "/chat";
            default:     return "/";
        }
    }

    /**
     * Which tab sent the guest here, for the copy. Defaults to a neutral
     * phrasing when the screen is opened directly.
     */
    _featureLabel()
    {
        switch ( this.routeParams.getQuery( "from", "" ) )
        {
            case "plan": return _lcs( "Dein Planer" );
            case "chat": return _lcs( "Der Chat" );
            default:     return _lcs( "Dieser Bereich" );
        }
    }

    _on( id, handler )
    {
        const el = this.app.view.pageContent.querySelector( '[data-id="' + id + '"]' );
        if ( el ) this.addEventListener( el, "click", handler );
    }
}
